import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import { useSnackbar } from "notistack";
import { useMultiTableData } from "../../../../components/tables/hooks/useMultiTableData.jsx";
import { applySortingWithAccessorKeys } from "../../../../components/tables/utils/tableUtils.js";
import { getDatum } from "@accredo/fe-shared/services/formatService.js";
import { useClaimStates } from "../../../../hooks/useClaimStates.js";
import ClaimStatusBadge from "../../../../components/ClaimStatusBadge.jsx";

/**
 * Alle Schäden eines Kunden (Default: alle Status), exakter Server-Filter über
 * kunde_id_ov (siehe KundeController::customerClaims() / ClaimRepository::getClaimsByCustomer()).
 * FilterTabs (Alle/gespeichert/in Bearbeitung/abgeschlossen) und Aktualisierung-Spalte
 * analog zu components/tables/ClaimsTable.jsx (dort zusätzlich mit Status-Zählern -
 * hier bewusst ohne, da das eine eigene kunden-skalierte Counts-Route bräuchte).
 */
export default function CustomerClaimsTable({ customerId }) {
  const { enqueueSnackbar } = useSnackbar();
  const claimStates = useClaimStates();
  const [activeFilterTab, setActiveFilterTab] = useState("all");

  const tableKey = "customerClaims";
  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Schäden",
        route: `/data/kundes/${customerId}/claims`,
        pageSize: 10,
        storageKey: "v2_table_customer_claims",
        extraParams: {
          include: ["objekt"],
          sort: [{ field: "updated_at", order: "desc" }],
          ...(activeFilterTab !== "all"
            ? { filters: { broad_status: activeFilterTab } }
            : {}),
        },
      },
    ],
    [customerId, activeFilterTab]
  );

  const { tableStates, updateTableState, fetchData } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );
  const tableState = tableStates[tableKey] || {};
  const rows = tableState.rows || [];

  useEffect(() => {
    fetchData(tableKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  // Neu laden, wenn zwischen Alle/gespeichert/in Bearbeitung/abgeschlossen gewechselt wird
  useEffect(() => {
    updateTableState(tableKey, { pageIndex: 0, shouldFetch: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilterTab]);

  const filterTabs = useMemo(
    () => [
      { key: "all", label: "Alle", onClick: setActiveFilterTab },
      { key: "saved", label: "gespeichert", onClick: setActiveFilterTab },
      {
        key: "processing",
        label: "in Bearbeitung",
        onClick: setActiveFilterTab,
      },
      { key: "closed", label: "abgeschlossen", onClick: setActiveFilterTab },
    ],
    []
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: "status",
        header: "Status",
        size: 160,
        filterVariant: "select",
        filterSelectOptions: claimStates?.states
          ? claimStates.states.map((item) => ({
              value: item.name,
              label: item.label,
            }))
          : [],
        Cell: ({ row }) => {
          const status = row.original.status;
          return (
            <ClaimStatusBadge
              status={status?.value || status}
              label={status?.label || status}
              size="small"
            />
          );
        },
      },
      {
        // accessorKeys überschreibt den Sortier-Feldnamen auf "objekt" (siehe
        // AllowedSort::callback('objekt', ...) in ClaimRepository::buildQuery),
        // da der Backend-Sort nur nach objekts.bezeichnung sortieren kann.
        accessorKey: "objekt.bezeichnung",
        id: "objekt.bezeichnung",
        accessorKeys: ["objekt"],
        header: "Objekt",
        size: 200,
        enableColumnFilter: false,
        Cell: ({ row }) => row.original.objekt?.bezeichnung || "—",
      },
      {
        // Kein Backend-Sort für die Objekt-Adresse vorgesehen (nur Bezeichnung
        // ist über den "objekt"-Sort sortierbar, siehe oben).
        accessorKey: "objekt.adresse",
        id: "objekt.adresse",
        enableColumnFilter: false,
        enableSorting: false,
        header: "Adresse",
        size: 200,
        Cell: ({ row }) => {
          const objekt = row.original.objekt;
          if (!objekt) {
            return "—";
          }
          const address = [
            [objekt.strasse, objekt.hausnr].filter(Boolean).join(" "),
            [objekt.plz, objekt.ort].filter(Boolean).join(" "),
          ]
            .filter(Boolean)
            .join(", ");
          return address || "—";
        },
      },
      {
        // Kein Backend-Datumsfilter für schaden_datum vorgesehen (bräuchte eine
        // eigene LocaleAwareDateFilter-Instanz analog zum bestehenden "period"-Filter).
        // Ebenso kein Backend-Sort für schaden_datum registriert.
        accessorKey: "schaden_datum",
        header: "Schadendatum",
        size: 130,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => getDatum(row.original.schaden_datum),
      },
      { accessorKey: "sparte", header: "Sparte", size: 150 },
      {
        // Backend-Filter ist unter dem Namen "tenant_number" registriert
        // (AllowedFilter::exact('tenant_number', 'schaden_nummer_accredo')),
        // id daher bewusst darauf gesetzt statt auf den Anzeige-Feldnamen.
        accessorKey: "schaden_nummer_accredo",
        id: "tenant_number",
        header: "Schadennummer",
        size: 150,
      },
      {
        // Kein Backend-Filter für schaden_hoehe (numerischer Betrag, ein sinnvoller
        // Bereichs-Filter wäre kein "schneller" Fix).
        accessorKey: "schaden_hoehe",
        header: "Schadenhöhe",
        size: 130,
        enableColumnFilter: false,
      },
      {
        // Kein Backend-Filter für updated_at vorgesehen (bräuchte eine eigene
        // LocaleAwareDateFilter-Instanz analog zum bestehenden "period"-Filter).
        accessorKey: "updated_at",
        header: "Aktualisierung",
        size: 120,
        enableColumnFilter: false,
        Cell: ({ row }) => getDatum(row.original.updated_at),
      },
      {
        accessorKey: "actions",
        header: "",
        size: 50,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: () => (
          <Box
            className="customer-claims-open-icon"
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              opacity: 0,
              transition: "opacity 0.15s",
            }}
          >
            <OpenInNewIcon fontSize="small" />
          </Box>
        ),
      },
    ],
    [claimStates]
  );

  return (
    <DataTable
      columns={columns}
      data={rows}
      initialState={{ columnVisibility: { "objekt.adresse": false } }}
      isLoading={tableState.isLoading}
      hasLoaded={tableState.hasLoaded}
      filterTabs={filterTabs}
      activeFilterTab={activeFilterTab}
      onRowClick={(row) => window.open(`/schaeden/${row.id}`, "_blank")}
      muiTableBodyRowProps={{
        sx: {
          "&:hover .customer-claims-open-icon": { opacity: 1 },
        },
      }}
      enablePagination
      manualPagination
      pageIndex={tableState.pageIndex ?? 0}
      pageSize={tableState.pageSize ?? 10}
      totalRows={tableState.totalRows ?? rows.length}
      onChangePage={(nextOrUpdater) => {
        const base = {
          pageIndex: tableState.pageIndex ?? 0,
          pageSize: tableState.pageSize ?? 10,
        };
        const next =
          typeof nextOrUpdater === "function"
            ? nextOrUpdater(base)
            : nextOrUpdater;
        if (!next) return;
        updateTableState(tableKey, {
          pageIndex: next.pageIndex,
          pageSize: next.pageSize,
        });
      }}
      onChangeSorting={(sorting) =>
        updateTableState(tableKey, {
          sorting: applySortingWithAccessorKeys(sorting, columns),
        })
      }
      onChangeFilters={(filters) => updateTableState(tableKey, { filters })}
      onChangeGlobalFilter={(searchword) =>
        updateTableState(tableKey, { searchword })
      }
      storageKey="v2_table_customer_claims"
    />
  );
}

CustomerClaimsTable.propTypes = {
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};
